import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '@/lib/api'
import { relative } from '@/lib/format'
import type { NotificationItem, Paged } from '@/types/api'
import { Button, IconButton, cx } from './ui/primitives'

/**
 * The in-app notification centre. In-app delivery is not a per-user preference -
 * email and push are - because this list is the record of what happened, and a
 * record with holes in it is worse than useless.
 */
export function NotificationBell() {
  const [unread, setUnread] = useState(0)
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<NotificationItem[]>([])
  const [loading, setLoading] = useState(false)
  const panel = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    let live = true

    async function poll() {
      try {
        const result = await api.get<{ unread: number }>('/notifications/unread-count')
        if (live) setUnread(result.unread)
      } catch {
        // A failed poll is not worth a toast. The next one is a minute away.
      }
    }

    void poll()

    // A minute, not a second. This is a badge, not a chat client, and the API is on
    // a B1 instance shared with everything else.
    const timer = window.setInterval(poll, 60_000)

    return () => {
      live = false
      window.clearInterval(timer)
    }
  }, [])

  useEffect(() => {
    if (!open) return

    function onPointerDown(event: MouseEvent) {
      if (!panel.current?.contains(event.target as Node)) setOpen(false)
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  async function toggle() {
    const next = !open
    setOpen(next)
    if (!next) return

    setLoading(true)
    try {
      const page = await api.get<Paged<NotificationItem>>('/notifications', { pageSize: 12 })
      setItems(page.items)
    } catch {
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  async function openItem(item: NotificationItem) {
    setOpen(false)

    try {
      await api.post(`/notifications/${item.id}/read`)
      setUnread((count) => Math.max(0, count - (item.readAt ? 0 : 1)))
    } catch {
      /* reading it is a convenience, not the point of the click */
    }

    if (item.linkUrl) {
      // The link is an absolute URL for email. In the app it is an internal route.
      const path = item.linkUrl.replace(/^https?:\/\/[^/]+/, '')
      navigate(path)
    }
  }

  async function markAllRead() {
    try {
      await api.post('/notifications/read-all')
      setUnread(0)
      setItems((current) => current.map((item) => ({ ...item, readAt: new Date().toISOString() })))
    } catch {
      /* the badge corrects itself on the next poll */
    }
  }

  return (
    <div className="relative" ref={panel}>
      <IconButton
        onClick={() => void toggle()}
        aria-expanded={open}
        label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
      >
        <BellMark />

        {/* Over the bell rather than beside it: the count is a property of the bell, and
            a badge that pushes the icon sideways moves the whole header cluster every
            time something arrives. */}
        {unread > 0 && (
          <span
            aria-hidden
            className={cx(
              'absolute right-1 top-1 grid place-items-center rounded-full',
              'bg-crit font-mono text-[10px] leading-none text-white',
              'ring-2 ring-surface',
              unread > 9 ? 'h-4 min-w-4 px-1' : 'size-4',
            )}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </IconButton>

      {open && (
        <div className="absolute right-0 z-40 mt-2 w-[min(92vw,380px)] rounded-lg border border-line bg-surface shadow-float">
          <div className="flex items-center justify-between border-b border-line-soft px-4 py-2.5">
            <span className="text-sm font-semibold">Notifications</span>
            {unread > 0 && (
              <Button variant="ghost" size="sm" onClick={() => void markAllRead()}>
                Mark all read
              </Button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && <p className="px-4 py-6 text-center text-[13px] text-muted">Loading…</p>}

            {!loading && items.length === 0 && (
              <p className="px-4 py-8 text-center text-[13px] text-muted">
                Nothing yet. Approvals and expiring quotations land here.
              </p>
            )}

            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => void openItem(item)}
                className={cx(
                  'block w-full border-b border-line-soft px-4 py-3 text-left last:border-0 hover:bg-surface-2',
                  !item.readAt && 'bg-accent-wash/40',
                )}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[13px] font-medium">{item.title}</span>
                  <span className="shrink-0 font-mono text-[11px] text-faint">
                    {relative(item.createdAt)}
                  </span>
                </div>
                <p className="mt-0.5 line-clamp-2 text-[13px] text-muted">{item.body}</p>
              </button>
            ))}
          </div>

          {/* The panel is a peek at the last twelve. Everything older, and the filters
              for finding one of them, live on their own screen. */}
          <div className="border-t border-line-soft px-4 py-2.5">
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                navigate('/notifications')
              }}
              className="text-[13px] font-medium text-accent hover:underline"
            >
              Open notification centre
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * A bell rather than the word "Alerts". The header is a row of controls, and the one that
 * carries a count has to be findable at a glance rather than read.
 */
function BellMark() {
  return (
    <svg aria-hidden viewBox="0 0 16 16" className="size-full">
      <path
        d="M8 1.75a3.75 3.75 0 0 0-3.75 3.75c0 3.05-1 4-1 4h9.5s-1-.95-1-4A3.75 3.75 0 0 0 8 1.75Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M6.6 12a1.5 1.5 0 0 0 2.8 0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  )
}
